import { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import {
    Alert,
    Box,
    Button, Typography,
    CircularProgress,
    Autocomplete,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";

const DailyStockReport = () => {
    const [productId, setProductId] = useState("");
    const [productOptions, setProductOptions] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [error, setError] = useState("");
    const columns = [
       // { field: "date", label: "Date", width: 120 },
        { field: "production_date", label: "Production Date",width: 100 },
        { field: "opening_stock", label: "Opening",width: 100 },
        { field: "stock_in", label: "Produced", width: 120 },
        { field: "dispatched_stock", label: "Dispatched", width: 110 },
        { field: "quantity_rejected", label: "Rejected1", width: 110 },
        { field: "rejected_stock", label: "Rejected2", width: 110 },
        { field: "rejectedatlevel2Today", label: "Rejected3", width: 110 },
        { field: "assembly_consumed", label: "Assembly", width: 110 },
        { field: "closing_stock", label: "Closing", width: 120 },
    ];
    // Fetch product list
    const fetchProductList = async () => {
        const token = getAuthToken();
        if (!token) {
            setError("Authentication token not found. Please log in again.");
            return;
        }
        setProductsLoading(true);
        try {
            const response = await axios.get(
                `${config.API}/product/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );
            const products =
                response?.data?.products?.map((product) => ({
                    value: product.id,
                    label: product.name,
                })) || [];

            setProductOptions(products);
        } catch (err) {
            console.error("Error fetching products:", err);
            setError(
                err.response?.data?.message ||
                    "Unable to load the product list."
            );
        } finally {
            setProductsLoading(false);
        }
    };
    useEffect(() => {
        fetchProductList();
    }, []);
    // Fetch daily stock report
    const fetchReport = async () => {
        if (!productId || !startDate || !endDate) {
            setError("Please select a product, From Date and To Date.");
            return;
        }
        if (startDate > endDate) {
            setError("From Date cannot be later than To Date.");
            return;
        }
        const token = getAuthToken();
        if (!token) {
            setError("Authentication token not found. Please log in again.");
            return;
        }
        setLoading(true);
        setError("");
        setReport([]);
        try {
            const response = await axios.post(
                `${config.API}/dispatch/openingclosingStock`,
                {
                    product_id: productId,
                    start_date: startDate,
                    end_date: endDate,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );
            if (response.data?.success) {
                setReport(response.data.report || []);
            } else {
                setError("Unable to fetch the stock report.");
            }
        } catch (err) {
            console.error("Daily stock report error:", err);

            setError(
                err.response?.data?.message ||
                    "Failed to load report. Please check the API endpoint."
            );
        } finally {
            setLoading(false);
        }
    };
    // Export report to PDF
    const exportPDF = () => {
        if (!report.length) {
            setError("Please load the report before exporting.");
            return;
        }
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
        });
        const selectedProduct = productOptions.find(
            (product) => String(product.value) === String(productId)
        );
        const productName = selectedProduct?.label || `Product ID: ${productId}`;
        doc.setFontSize(16);
        doc.text("Daily Stock Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Product: ${productName}`, 14, 22);
        doc.text(`From: ${startDate}    To: ${endDate}`, 14, 28);
        autoTable(doc, {
            startY: 34,
            head: [columns.map((column) => column.label)],
            body: report.map((row) =>
                columns.map((column) => {
                    const value = row[column.field];

                    if (column.field === "production_date") {
                        return value || "No Data";
                    }

                    return value ?? 0;
                })
            ),
            theme: "grid",
            styles: {
                fontSize: 7,
                cellPadding: 1,
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [41, 75, 120],
                textColor: 255,
                fontStyle: "bold",
            },
            margin: {
                left: 8,
                right: 8,
            },
        });

        doc.save(
            `Daily_Stock_Report_${startDate}_to_${endDate}.pdf`
        );
    };

    return (
       <>
    <Box
        sx={{
            px: 1.5,
            py: 1,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderLeft: "4px solid",
            borderColor: "primary.main",
            bgcolor: "background.paper",
            borderRadius: 1,
        }}
    >
        <AssessmentOutlinedIcon color="primary" />
        <Typography
            variant="h6"
            sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: "text.primary",
                lineHeight: 1.4,
            }}
        >
            Generate Report
        </Typography>
    </Box>

    <Box sx={{ p: 1 }}>
        <Paper sx={{ p: 1, mb: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                }}
            >

                    {/* Product Autocomplete */}
                    <Autocomplete
                        options={productOptions}
                        getOptionLabel={(option) => option.label || ""}
                        value={productOptions.find(
                            (product) => String(product.value) === String(productId)
                        ) || null}
                        onChange={(_, selectedProduct) => {
                            setProductId(selectedProduct ? selectedProduct.value : "");
                            setReport([]);
                            setError("");
                        } }
                        loading={productsLoading}
                        disabled={productsLoading}
                        isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Product"
                                size="small"
                                placeholder="Search product..." />
                        )}
                        sx={{ minWidth: 300 }} />

                    {/* From date */}
                    <TextField
                        label="From Date"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        slotProps={{
                            inputLabel: { shrink: true },
                        }} />

                    {/* To date */}
                    <TextField
                        label="To Date"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        slotProps={{
                            inputLabel: { shrink: true },
                        }} />

                    {/* View report */}
                    <Button
                        variant="contained"
                        onClick={fetchReport}
                        disabled={loading || productsLoading}
                    >
                        {loading ? (
                            <CircularProgress
                                size={22}
                                color="inherit" />
                        ) : (
                            "View Report"
                        )}
                    </Button>

                    {/* PDF export */}
                    <Button
                        variant="outlined"
                        onClick={exportPDF}
                        disabled={!report.length || loading}
                    >
                        Export PDF
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 1 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            <TableContainer
                component={Paper}
                sx={{ overflowX: "auto" }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    sx={{
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                        backgroundColor: "#f2f4f7",
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {report.length > 0 ? (
                            report.map((row, index) => (
                                <TableRow
                                    key={`${row.date}-${index}`}
                                    hover
                                >
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.field}
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            {column.field === "production_date"
                                                ? row[column.field] || "No Data"
                                                : row[column.field] ?? 0}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    align="center"
                                >
                                    {loading
                                        ? "Loading report..."
                                        : "Select a product and date range, then click View Report."}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box></>
    );
};

export default DailyStockReport;
